import Company from '../interfaces/Company';
import { Price } from '../interfaces/Price';

const TIER_RANK: Record<string, number> = {
  free: 0,
  starter: 1,
  business: 2,
  enterprise: 3,
};

function tierRank(tier: string | undefined): number {
  if (!tier) return 0;
  return TIER_RANK[tier] ?? 0;
}

export function isEnterpriseCustomer(
  company: Company | null | undefined
): boolean {
  if (!company) return false;
  if (company.enterprise === true) return true;
  if (company.plan_tier === 'enterprise') return true;
  if (company.plan_id?.toLowerCase().includes('enterprise')) return true;
  return false;
}

/** Banner when chat-only org opens Call plans — only Enterprise can add calls. */
export function shouldShowChatUserCallEnterpriseNotice(
  company: Company | null | undefined,
  v2Tab: 'call' | 'chat' | 'bundle'
): boolean {
  if (!company || v2Tab !== 'call') {
    return false;
  }
  if (company.plan_type !== 'chat') return false;
  return !isEnterpriseCustomer(company);
}

export type PricingV2BlockReason =
  | 'chat_add_calls_requires_enterprise'
  | 'chat_downgrade_not_here';

export function getPricingV2CardBlock(
  company: Company | null | undefined,
  activeV2Tab: 'call' | 'chat' | 'bundle' | undefined,
  plan: Price
): { blocked: false } | { blocked: true; reason: PricingV2BlockReason } {
  if (!company || !activeV2Tab) {
    return { blocked: false };
  }

  if (isEnterpriseCustomer(company)) {
    return { blocked: false };
  }

  if (company.plan_type === 'bundle') {
    return { blocked: false };
  }

  // Chat-only org adding call capacity on the call line: only Enterprise is allowed.
  // Bundle upgrades are allowed on the bundle tab (call + chat in one plan).
  if (
    company.plan_type === 'chat' &&
    activeV2Tab === 'call' &&
    plan.planType === 'call' &&
    plan.tier !== 'enterprise'
  ) {
    return { blocked: true, reason: 'chat_add_calls_requires_enterprise' };
  }

  // Chat tab: block downgrades (higher tier → lower tier)
  if (
    company.plan_type === 'chat' &&
    activeV2Tab === 'chat' &&
    plan.planType === 'chat' &&
    plan.tier
  ) {
    const current = company.plan_tier ?? 'free';
    if (tierRank(plan.tier) < tierRank(current)) {
      return { blocked: true, reason: 'chat_downgrade_not_here' };
    }
  }

  return { blocked: false };
}
